import React, {
  createRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  IsMobile,
  vhPlayerSupportsDevice,
  WeSupportUnity,
} from '../../../../../../utility/BrowserCheck';
import { Unity } from 'react-unity-webgl';
import PropTypes from 'prop-types';
import * as Styled from './UnityVHPlayer.styled';
import _ from 'lodash';
import {
  addResult,
  currentVHStateSelector,
  selectAudioToPlay,
  setVirtualHumanTalking,
} from '../../../slices/chat/ChatSlice';
import { useDispatch, useSelector } from 'react-redux';
import { FindGetParameter } from '../../../../../../utility/URLHelpers';
import { UnityLoader } from '../../../../../../components/Unity';
import { useEffectOnlyOnce } from '../../../../../../utility/CustomHooks';
import { useVHTypeConfig } from '../../vh-types/vhTypeHooks';
import VhTypes from '../../vh-types/configs/VHTypes';
import StateEntryAudio from '../../StateEntryAudio/StateEntryAudio';
import {
  isFullscreenSelector,
  setMoreInfoVisible,
} from '../../../slices/vh-player/VHPlayerSlice';

import VHInfoPopup from '../../VHInfoPopup/VHInfoPopup';
import { usePrevious } from '../../../utility/StateHooks';
import { deepLinkValueContext } from '../../../../../core/src/components/DeepLink/DeepLinkProvider';
import { Button } from '@virtidev/toolbox';
import useVHUnityContext from './useVHUnityContext';
import { useFeature } from '../../../../../core/src/components/LaunchDarkly';
import * as Sentry from '@sentry/browser';
import { useFlashMessage } from '../../../../../core/src/components/FlashMessage';
import { isVHAudioStreamService } from '@vh/utility/isVHAudioStreamService';
import { virtualHumanDataSelector } from '@vh/slices/VirtualHumanSlice';
import { useAutoStartMicStream } from '@core/components/MicrophoneStreamProvider/useAutoStartMicStream';
import { useTranslation } from 'react-i18next';
import LegacyVHInfoPopup from '@vh/components/VHInfoPopup/LegacyVHInfoPopup';
import { VHPlayerEventBusContext } from '@vh/contexts/VHPlayerEventBus/VHPlayerEventBus.context';

const MAX_HEIGHT = 420;
const MIN_WIDTH = 420;
const MAX_WIDTH = (MAX_HEIGHT / 9) * 16;

const PDUnityInner = ({
  onLoaded,
  currentVHState,
  audioToPlay,
  onVirtualHumanStartTalking,
  onExamComplete,
  onVirtualHumanFinishedTalking,
  pendingAction,
  vhType,
  avatarName,
  clothing,
  environment,
  pdAreaRef,
  isFullscreen,
  patientId,
}) => {
  const deepLink = useContext(deepLinkValueContext);
  const {
    unityProvider,
    addEventListener,
    removeEventListener,
    sendMessage,
    requestFullscreen,
    loadingProgression,
  } = useVHUnityContext();

  const unityWrapperRef = useRef(null);
  const vhClothingFeature = useFeature('vh-clothing');

  const [progress, setProgress] = useState(0);
  const [loadingStage, setLoadingStage] = useState('initial');
  const [dimensions, setDimensions] = useState({
    height: MAX_HEIGHT,
    width: (MAX_HEIGHT / 9) * 16,
  });
  const [hasResizedOnProgress, setHasResizedOnProgress] = useState(false);

  const resize = useCallback(() => {
    let width = Math.min(
      MAX_WIDTH,
      pdAreaRef ? pdAreaRef.clientWidth * 0.6 : 999999
    );
    width = Math.max(width, MIN_WIDTH);
    let height = (width / 16) * 9;
    if (
      Math.abs(width - this.state.width) > 0 ||
      Math.abs(height - this.state.height) > 0
    ) {
      setDimensions({ width, height });
    }
  }, [pdAreaRef]);

  const handleOnLoaded = useCallback(() => {
    resize();
    // this may be redundant?
    setLoadingStage(false);
  }, [resize, setLoadingStage]);

  useEffect(() => {
    addEventListener('loaded', handleOnLoaded);
    return () => {
      removeEventListener('loaded', handleOnLoaded);
    };
  }, [addEventListener, handleOnLoaded, removeEventListener]);

  const handleOnPlayerLoaded = useCallback(
    (userName, score) => {
      const fallbackEnvironment =
        vhType === 'medical' ? 'PatientRoom' : 'Office';
      const avatarAndEnvironment = JSON.stringify({
        avatarName: avatarName,
        environmentName: environment?.Name ?? fallbackEnvironment,
        backdropURL: environment?.URL,
        clothing: vhClothingFeature && clothing ? clothing : undefined,
      });
      sendMessage(
        'WebBridge',
        'SelectAvatarAndEnvironment',
        avatarAndEnvironment
      );
      setLoadingStage('assets');
    },
    [
      vhType,
      avatarName,
      environment?.Name,
      environment?.URL,
      vhClothingFeature,
      clothing,
      sendMessage,
    ]
  );

  useEffect(() => {
    addEventListener('PlayerLoaded', handleOnPlayerLoaded);
    return () => {
      removeEventListener('PlayerLoaded', handleOnPlayerLoaded);
    };
  }, [addEventListener, handleOnPlayerLoaded, removeEventListener]);

  const handleOnAvatarReady = useCallback(() => {
    setLoadingStage(false);
    onLoaded();
  }, [setLoadingStage, onLoaded]);

  useEffect(() => {
    addEventListener('AvatarReady', handleOnAvatarReady);
    return () => {
      removeEventListener('AvatarReady', handleOnAvatarReady);
    };
  }, [addEventListener, handleOnAvatarReady, removeEventListener]);

  const handleOnExamComplete = useCallback(() => {
    onExamComplete();
  }, [onExamComplete]);

  useEffect(() => {
    addEventListener('ExamComplete', handleOnExamComplete);
    return () => {
      removeEventListener('ExamComplete', handleOnExamComplete);
    };
  }, [addEventListener, handleOnExamComplete, removeEventListener]);

  const handlePatientFinishedTalking = useCallback(
    (userName, score) => {
      onVirtualHumanFinishedTalking();
    },
    [onVirtualHumanFinishedTalking]
  );

  const { addFlashMessage } = useFlashMessage();
  const vh = useSelector(virtualHumanDataSelector);
  const isStreamableService = isVHAudioStreamService(vh?.VHVoice?.Service);

  const { on } = useContext(VHPlayerEventBusContext);
  useEffect(() => {
    const subscribe = on('StopVirtualHumanTalking', () => {
      console.log('StopVirtualHumanTalking');
      sendMessage('WebBridge', 'StopVirtualHumanTalking');
    });
    return subscribe;
  }, [on, sendMessage]);

  useEffect(() => {
    addEventListener('PatientFinishedTalking', handlePatientFinishedTalking);
    return () => {
      removeEventListener(
        'PatientFinishedTalking',
        handlePatientFinishedTalking
      );
    };
  }, [addEventListener, handlePatientFinishedTalking, removeEventListener]);

  useEffect(() => {
    // send conversation item audio to unity to render
    if (audioToPlay) {
      console.log('audiotoplay');
      console.log(audioToPlay);
      let parsed;
      // only do the start and end events for vh talking
      // if there's actual audio to play
      try {
        parsed = JSON.parse(audioToPlay);
      } catch (e) {
        console.error('Failed to parse audioToPlay');
        Sentry.captureException(e, {
          extra: {
            audioToPlay,
          },
        });
      }
      if (parsed?.response?.audio?.url || isStreamableService) {
        onVirtualHumanStartTalking();
        try {
          sendMessage('WebBridge', 'SendBufferToUnity', audioToPlay);
        } catch (e) {
          console.log('error when sending audio to Unity');
        }
      } else {
        addFlashMessage(
          'Sorry, we failed to generate the audio for this message',
          'warning'
        );
        console.log('Audio failed to generate');
        Sentry.captureMessage('No audio url was provided', {
          level: 'error',
          extra: {
            vhId: patientId,
            audioToPlay,
          },
        });
      }
    }
  }, [
    audioToPlay,
    onVirtualHumanStartTalking,
    sendMessage,
    addFlashMessage,
    patientId,
    isStreamableService,
  ]);

  const prevCurrentVHState = usePrevious(currentVHState);
  useEffect(() => {
    // if state has changed
    if (
      currentVHState &&
      prevCurrentVHState &&
      currentVHState.id !== prevCurrentVHState.id &&
      currentVHState.emotion &&
      FindGetParameter('unitydevbuild') === 'true'
    ) {
      try {
        sendMessage(
          'WebBridge',
          'SendAnimationToUnity',
          currentVHState?.emotion?.name ?? ''
        );
      } catch (e) {
        console.error(
          'error when sending emotion ' +
            (currentVHState?.emotion?.name ?? '') +
            ' to Unity'
        );
      }
    }
  }, [currentVHState, prevCurrentVHState, sendMessage]);

  useEffect(() => {
    if (pendingAction?.actionKey) {
      sendMessage('WebBridge', 'SendAnimationToUnity', pendingAction.actionKey);
    }
  }, [pendingAction, sendMessage]);
  const { t } = useTranslation();

  const goToDeepLink = useCallback(() => {
    window.onbeforeunload = null;
    window.location = deepLink;
  }, [deepLink]);

  if (!vhPlayerSupportsDevice('3D')) {
    return (
      <Styled.Message>
        Unfortunately, web preview is currently only available in latest version
        of Google Chrome, Mozilla Firefox, and Microsoft Edge on a computer.
        {deepLink && (
          <Button color="primary" onClick={goToDeepLink}>
            Use the Virti App
          </Button>
        )}
      </Styled.Message>
    );
  }
  let loadingFlavourTexts = [
    t('constructingPersonality'),
    t('generatingEnvironment'),
    t('repositioningProps'),
    t('growingVirtualBrain'),
    t('educating'),
  ];
  if (vhType === 'medical') {
    loadingFlavourTexts = [
      t('layingHospitalBricks'),
      t('constructingPatientBed'),
      t('activatingPatientGown'),
      t('growingPatientBrain'),
      t('buildingPatientPersonality'),
    ];
  }

  return (
    <Styled.UnityWrapper ref={unityWrapperRef} $isFullscreen={isFullscreen}>
      {loadingStage !== false && (
        <UnityLoader
          loadingFlavourTexts={loadingFlavourTexts}
          progress={Math.round(loadingProgression * 100)}
          loadingStage={loadingStage}
        />
      )}
      <Unity
        width="1920"
        height="1080"
        unityProvider={unityProvider}
        className="unity-player"
      />
    </Styled.UnityWrapper>
  );
};

PDUnityInner.defaultProps = {
  rootDirectory: '/',
  vhType: VhTypes.DEFAULT,
  isFullscreen: false,
};

PDUnityInner.propTypes = {
  rootDirectory: PropTypes.string.isRequired,
  audioToPlay: PropTypes.string,
  onLoaded: PropTypes.func.isRequired,
  wrapperWidth: PropTypes.number.isRequired,
  pdAreaRef: PropTypes.object,
  avatarName: PropTypes.string.isRequired,
  clothing: PropTypes.string,
  pendingAction: PropTypes.object,
  vhType: PropTypes.string.isRequired,
  environment: PropTypes.object.isRequired,
  currentVHState: PropTypes.object,
  isFullscreen: PropTypes.bool,
};

/**
 * @type {React.FC<{
 *   onLoaded: () => void;
 *   rootDirectory: string;
 *   patientId: number;
 *   playerAreaRef: React.RefObject<HTMLDivElement> | null;
 *   avatarName: string;
 *   environment: object;
 *   clothing?: string;
 * }>}
 * */
const UnityVHPlayer = (props) => {
  const { onLoaded } = props;
  // set here since PDUnity isn't a functional component D:
  const audioToPlay = useSelector(selectAudioToPlay);
  const dispatch = useDispatch();
  const wrapperRef = useRef(null);
  const pendingAction = useSelector((state) => state.chat.pendingAction);
  const vhTypeConfig = useVHTypeConfig();
  const currentVHState = useSelector(currentVHStateSelector);
  useAutoStartMicStream(false);
  const setReadyToStart = useCallback(() => {
    dispatch(setMoreInfoVisible(true));
  }, [dispatch]);
  const isFullscreen = useSelector(isFullscreenSelector);

  const handleLoaded = useCallback(() => {
    if (typeof onLoaded === 'function') {
      onLoaded();
    }
    if (localStorage.getItem('debug_vh') !== 'true') {
      setReadyToStart(true);
    }
  }, [setReadyToStart, onLoaded]);

  const handleVirtualHumanStartTalking = useCallback(() => {
    dispatch(setVirtualHumanTalking(true));
  }, [dispatch]);
  const handleVirtualHumanFinishTalking = useCallback(() => {
    dispatch(setVirtualHumanTalking(false));
  }, [dispatch]);
  const showSelectableUserLanguageFeature = useFeature(
    'show-selectable-user-language'
  );

  return (
    <Styled.Wrapper ref={wrapperRef} $isFullscreen={isFullscreen}>
      <StateEntryAudio />
      {showSelectableUserLanguageFeature ? (
        <VHInfoPopup vhId={props.patientId} />
      ) : (
        <LegacyVHInfoPopup vhId={props.patientId} />
      )}
      <PDUnityInner
        {...props}
        vhType={vhTypeConfig.type}
        audioToPlay={audioToPlay}
        pendingAction={pendingAction}
        currentVHState={currentVHState}
        wrapperWidth={
          wrapperRef && wrapperRef.current
            ? wrapperRef.current.clientWidth
            : MAX_WIDTH
        }
        pdAreaRef={props.pdAreaRef}
        onExamComplete={() => {
          // add result now that exam has been complete
          dispatch(addResult(pendingAction));
        }}
        onVirtualHumanStartTalking={handleVirtualHumanStartTalking}
        onVirtualHumanFinishedTalking={handleVirtualHumanFinishTalking}
        onLoaded={handleLoaded}
        isFullscreen={isFullscreen}
      />
    </Styled.Wrapper>
  );
};

UnityVHPlayer.propTypes = {
  rootDirectory: PropTypes.string.isRequired,
  audioToPlay: PropTypes.string,
  patientId: PropTypes.number.isRequired,
  pdAreaRef: PropTypes.object,
  avatarName: PropTypes.string.isRequired,
  environment: PropTypes.object.isRequired,
  clothing: PropTypes.string,
};

export default UnityVHPlayer;
